import type { DynamicErpFieldsPayload, ErpFieldValue, TenantErpFieldConfig } from '@schemas/api';
import { useErpFieldOptions } from '@api/hooks/useErpFields';
import { SearchableSelect } from '@components/common/SearchableSelect';

interface DynamicErpFieldsEditorProps {
  payload?: DynamicErpFieldsPayload;
  values: ErpFieldValue[];
  onChange: (values: ErpFieldValue[]) => void;
  surface?: string;
  valueScope?: string;
  disabled?: boolean;
  showDescriptions?: boolean;
  showSourceBadge?: boolean;
}

const valueForConfig = (values: ErpFieldValue[], config: TenantErpFieldConfig) =>
  values.find((value) => value.field_key === config.field.field_key);

const isCarriedFromDocumentReview = (config: TenantErpFieldConfig) =>
  Boolean((config.extra || {}).carried_from_document_review);

const DynamicErpReferenceField = ({
  config,
  value,
  onChange,
  disabled = false,
}: {
  config: TenantErpFieldConfig;
  value?: ErpFieldValue;
  onChange: (value: ErpFieldValue) => void;
  disabled?: boolean;
}) => {
  const optionsQuery = useErpFieldOptions({
    field_definition_id: config.field_definition_id,
    object_type: config.field.object_type || undefined,
    erp_connection_id: config.field.erp_connection_id || undefined,
    limit: 100,
  });
  const options = optionsQuery.data?.items || [];

  return (
    <SearchableSelect
      value={value?.reference_option_id || ''}
      disabled={disabled}
      onChange={(selectedValue) => {
        const option = options.find((item) => item.id === selectedValue);
        onChange({
          field_definition_id: config.field_definition_id,
          field_key: config.field.field_key,
          value: option?.external_id || '',
          display_value: option?.label || '',
          reference_option_id: option?.id || null,
          source: 'manual',
          evidence: {},
        });
      }}
      options={[{ id: '', label: 'Select...' }, ...options.map((option) => ({ id: option.id, label: option.label }))]}
      placeholder="Select..."
      clearable={false}
      className="mt-1"
    />
  );
};

export const DynamicErpFieldsEditor = ({
  payload,
  values,
  onChange,
  surface,
  valueScope,
  disabled = false,
  showDescriptions = false,
  showSourceBadge = false,
}: DynamicErpFieldsEditorProps) => {
  const configs = (payload?.fields || []).filter((config) => {
    if (!config.enabled) return false;
    if (surface && config.surface !== surface) return false;
    if (valueScope && config.field.value_scope !== valueScope) return false;
    return true;
  });

  const updateValue = (nextValue: ErpFieldValue) => {
    const withoutCurrent = values.filter((value) => value.field_key !== nextValue.field_key);
    onChange([...withoutCurrent, nextValue]);
  };

  if (configs.length === 0) {
    return null;
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {configs.map((config) => {
        const current = valueForConfig(values, config);
        const isReference = config.field.value_type === 'reference' || config.field.value_type === 'multi_reference';
        return (
          <label key={config.id} className="block rounded-md border border-gray-200 bg-white p-3 text-sm dark:border-gray-700 dark:bg-gray-900">
            <span className="flex flex-wrap items-center gap-2 font-medium text-gray-800 dark:text-gray-200">
              <span>{config.field.label}{config.required ? ' *' : ''}</span>
              {showSourceBadge && isCarriedFromDocumentReview(config) && (
                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                  from review
                </span>
              )}
            </span>
            {showDescriptions && config.field.description && (
              <span className="mt-1 block text-xs leading-5 text-gray-500 dark:text-gray-400">
                {config.field.description}
              </span>
            )}
            {isReference ? (
              <DynamicErpReferenceField
                config={config}
                value={current}
                onChange={updateValue}
                disabled={disabled}
              />
            ) : (
              <input
                type={config.field.value_type === 'number' ? 'number' : config.field.value_type === 'date' ? 'date' : 'text'}
                value={String(current?.display_value ?? current?.value ?? '')}
                disabled={disabled}
                onChange={(event) => updateValue({
                  field_definition_id: config.field_definition_id,
                  field_key: config.field.field_key,
                  value: event.target.value,
                  display_value: event.target.value,
                  source: 'manual',
                  evidence: {},
                })}
                className="input mt-1 disabled:cursor-not-allowed disabled:opacity-60"
              />
            )}
          </label>
        );
      })}
    </div>
  );
};
